import { drawing as d } from '@progress/kendo-drawing';
import { fitToSize, sizeOptionsOrDefault } from './utils';
import { Rectangle } from './Rectangle';
const cp1XRatio = 5 / 6;
const cp2XRatio = 1 / 6;
const docDefaultOptions = {
    waveRatio: 0.37
};
const multyDocsDefaultOptions = {
    ...docDefaultOptions,
    docsXRatio: 0.14,
    docsYRatio: 0.2,
};
/**
 * @hidden
 * Draws a document shape with a wavy bottom edge.
 * @param drawingElement - The drawing element to draw on
 * @param origin - Starting position coordinates
 * @param width - Width of the document
 * @param height - Height of the document
 * @param waveRatio - Ratio for the wave amplitude
 * @param cornerRadius - Corner radius for rounded corners
 */
const drawDocument = (drawingElement, origin, width, height, waveRatio, cornerRadius) => {
    const amplitude = height * waveRatio;
    drawingElement
        .moveTo(origin[0] + width, origin[1] + height)
        .curveTo([origin[0] + width, origin[1] + height], [origin[0] + width * cp1XRatio, origin[1] + height - amplitude], [origin[0] + width / 2, origin[1] + height])
        .curveTo([origin[0] + width * cp2XRatio, origin[1] + height + amplitude], [origin[0], origin[1] + height], [origin[0], origin[1] + height])
        .lineTo(origin[0], origin[1] + cornerRadius)
        .arcTo([origin[0] + cornerRadius, origin[1]], cornerRadius, cornerRadius, true, false)
        .lineTo(origin[0] + width - cornerRadius, origin[1])
        .arcTo([origin[0] + width, origin[1] + cornerRadius], cornerRadius, cornerRadius, true, false)
        .lineTo(origin[0] + width, origin[1] + height)
        .close();
};
/**
 * Represents a Document shape, a flowchart element used to indicate documents
 * or printed output. Extends Rectangle with a wavy bottom edge.
 */
export class Document extends Rectangle {
    /**
     * Creates a new Document instance.
     * @param options - Configuration options for the document shape
     */
    constructor(options) {
        super({ ...docDefaultOptions, ...options });
    }
    /**
     * @hidden
     * Draws the document shape path with a wavy bottom edge.
     */
    _drawPath() {
        const { width, height } = sizeOptionsOrDefault(this.options);
        const strokeWidth = this.options.stroke.width || 0;
        const { waveRatio, cornerRadius } = this.options;
        const halfAmplitude = height * waveRatio * 0.5;
        drawDocument(this.drawingElement, [0, 0], width, height - halfAmplitude, waveRatio, cornerRadius);
        fitToSize(this.drawingElement, width, height, strokeWidth);
    }
}
/**
 * Represents a Multiple Documents shape, indicating multiple documents or pages.
 * Extends Document with additional overlapping document shapes.
 */
export class MultipleDocuments extends Document {
    /**
     * Creates a new MultipleDocuments instance.
     * @param options - Configuration options for the multiple documents shape
     */
    constructor(options) {
        super({ ...multyDocsDefaultOptions, ...options });
    }
    /**
     * @hidden
     * Initializes the path using a group for multiple document shapes.
     */
    _initPath() {
        this.drawingElement = new d.Group({ id: this.options.id });
        this._fill();
        this._drawPath();
    }
    /**
     * @hidden
     * Fills the multiple document shapes with the specified fill options.
     * @param fillOptions - Fill options to apply
     */
    _fill(fillOptions) {
        const fill = this._evalFillOptions(fillOptions);
        if (fill.gradient) {
            const gradient = fill.gradient;
            const GradientClass = (gradient.type === 'radial' ? d.RadialGradient : d.LinearGradient);
            this.drawingElement.children.forEach((child) => {
                child.fill(new GradientClass(gradient));
            });
        }
        else {
            this.drawingElement.children.forEach((child) => {
                child.fill(fill.color, fill.opacity);
            });
        }
    }
    /**
     * @hidden
     * Draws multiple overlapping document shapes.
     */
    _drawPath() {
        const { width, height } = sizeOptionsOrDefault(this.options);
        const strokeWidth = this.options.stroke.width || 0;
        const { waveRatio, docsXRatio, docsYRatio, stroke, fill, cornerRadius } = this.options;
        const docOffsetX = width * docsXRatio;
        const docOffsetY = height * docsYRatio;
        const drawingElement = this.drawingElement;
        const path1 = new d.MultiPath({ stroke, fill });
        const path2 = new d.MultiPath({ stroke, fill });
        const halfAmplitude = height * waveRatio * 0.5;
        drawDocument(path1, [docOffsetX, 0], width - docOffsetX, height - docOffsetY - halfAmplitude, waveRatio, cornerRadius);
        drawDocument(path2, [0, docOffsetY], width - docOffsetX, height - docOffsetY - halfAmplitude, waveRatio, cornerRadius);
        drawingElement.append(path1);
        drawingElement.append(path2);
        fitToSize(this.drawingElement, width, height, strokeWidth);
    }
}
